import _ from "lodash";
import {
    BehaviorSubject,
    Observable,
    catchError,
    filter,
    first,
    map,
    of,
    switchMap,
    throwError,
} from "rxjs";
import { AjaxError, AjaxResponse, ajax } from "rxjs/ajax";
import { ILoginResponseDto } from "../features/auth/interface/dto/login-response.dto";
import { ITokenPair } from "../features/auth/interface/dto/token-pair";
import {
    authRedirect,
    authRequestAccess,
} from "../features/auth/redux/auth.actions";
import { setAuthTokens } from "../features/auth/selectors/auth.selector";
import { IErrorDto } from "../interface/dto/error.dto";
import { IMapObject } from "../interface/map-object";
import store from "../redux/store";
import { logError } from "../util/logger";

const apiURL = process.env.REACT_APP_API_URL ?? "";
type RequestParams = IMapObject<string | number | boolean>;

export const get = <T>(
    url: string,
    params?: IMapObject<string | number>,
    token?: ITokenPair,
): Observable<T> =>
    request<T>({
        url,
        params,
        token,
    });

export const post = <T, R = unknown>(
    url: string,
    body: R,
    params?: RequestParams,
    token?: ITokenPair,
): Observable<T> =>
    request<T, R>({
        url,
        params,
        method: "POST",
        body,
        token,
    });

export const remove = <T>(
    url: string,
    params?: IMapObject<string | number>,
    token?: ITokenPair,
): Observable<T> =>
    request<T>({
        url,
        params,
        method: "DELETE",
        token,
    });

export const put = <T, R = unknown>(
    url: string,
    body: R,
    params?: IMapObject<string | number>,
    token?: ITokenPair,
): Observable<T> =>
    request<T, R>({
        url,
        params,
        method: "PUT",
        body,
        token,
    });

interface IRequest<R> {
    method?: "GET" | "POST" | "PUT" | "DELETE";
    params?: RequestParams;
    body?: R;
    url: string;
    token?: ITokenPair;
}

const needToRefresh = (accessToken: string | undefined) => {
    if (accessToken) {
        const [, payload] = accessToken.split(".");
        if (payload) {
            try {
                const { exp } = JSON.parse(atob(payload)) as { exp: number };
                return new Date().valueOf() / 1000 > exp;
            } catch (e) {
                console.error(e);
            }
        }
    }
    return false;
};

let tokenRefreshInProgress = false;
const tokenRefreshSubject = new BehaviorSubject<ILoginResponseDto | null>(null);

export const validateAccessToken = <T>(
    token: ITokenPair | undefined,
    requestHandler: (accessToken: string | undefined) => Observable<T>,
    errorHandler: (error: AjaxError) => Observable<never>,
) => {
    if (
        needToRefresh(token?.accessToken) &&
        token?.refreshToken &&
        token?.refreshToken !== token?.accessToken
    ) {
        if (tokenRefreshInProgress) {
            return tokenRefreshSubject
                .pipe(
                    filter((result) => result !== null),
                    first(),
                )
                .pipe(
                    switchMap((tokens) => requestHandler(tokens?.accessToken)),
                );
        } else {
            tokenRefreshInProgress = true;
            tokenRefreshSubject.next(null);
            return ajax<ILoginResponseDto>({
                url: `${apiURL}/auth/refresh`,
                method: "GET",
                crossDomain: true,
                headers: {
                    Authorization: `Bearer ${token.refreshToken}`,
                    Accept: "application/json",
                },
            }).pipe(
                map(
                    ({ response }: AjaxResponse<ILoginResponseDto>) => response,
                ),
                catchError(errorHandler),
                switchMap((response) => {
                    setAuthTokens(response.accessToken, response.refreshToken);
                    tokenRefreshInProgress = false;
                    tokenRefreshSubject.next(response);
                    return requestHandler(response.accessToken);
                }),
            );
        }
    }

    return requestHandler(token?.accessToken);
};

export const request = <T, R = undefined>({
    method = "GET",
    params,
    body,
    url,
    token,
}: IRequest<R>): Observable<T> => {
    const errorHandler = (error: AjaxError) => {
        if (error.status === 401) {
            store.dispatch(authRedirect());
            return of();
        }

        if (error.status === 403) {
            store.dispatch(authRequestAccess());
            return of();
        }

        const {
            message = "Internal Error",
            status,
            error: responseError = undefined,
        } = error.response as IErrorDto;

        logError(`Error at API call, message: ${message}`);

        return throwError(() => ({
            message,
            status: status || error.status || 500,
            error: responseError,
        }));
    };

    const requestHandler = (token?: string) => {
        const headers: Record<string, string> = {};
        if (token) {
            headers.Authorization = `Bearer ${token}`;
            headers.Accept = "application/json";
            if (body) {
                headers["Content-Type"] = "application/json";
            }
        }

        return ajax<T>({
            url: `${apiURL}${url}${toQueryString(params)}`,
            method,
            crossDomain: true,
            body: body ? JSON.stringify(body) : undefined,
            headers,
        }).pipe(
            map(({ response }: AjaxResponse<T>) => response),
            catchError(errorHandler),
        );
    };

    return validateAccessToken(token, requestHandler, errorHandler);
};

const toQueryString = (obj: RequestParams | undefined) =>
    obj && !_.isEmpty(obj)
        ? `?${_.map(
              obj,
              (value, key) => `${key}=${encodeURIComponent(value)}`,
          ).join("&")}`
        : "";
